import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import {
    Campaign,
    FacebookConnectResult,
    FacebookForm,
    FacebookStatus,
    LinkFacebookFormRequest,
} from './types';

const CAMPAIGNS_KEY = 'campaigns';
const FACEBOOK_KEY = 'facebook';

export const useCampaignFacebookStatus = (options?: { enabled?: boolean }) => {
    return useQuery<ApiResponse<FacebookStatus>>({
        queryKey: [CAMPAIGNS_KEY, FACEBOOK_KEY, 'status'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<FacebookStatus>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.FACEBOOK_STATUS
            );
            return data;
        },
        enabled: options?.enabled ?? true,
    });
};

/**
 * Validates the System User token saved in Integrations and creates/refreshes the
 * tenant's Facebook connection and pages. Also serves as "Sync pages" once connected.
 * On 422 the axios error carries `{ errors: string[], missing_permissions?: string[] }`
 * (see `parseFacebookConnectError`).
 */
export const useConnectCampaignFacebook = () => {
    const queryClient = useQueryClient();

    return useMutation<FacebookConnectResult, Error, void>({
        mutationFn: async () => {
            const { data } = await httpClient.post<ApiResponse<FacebookConnectResult>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.FACEBOOK_CONNECT
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, FACEBOOK_KEY, 'status'] });
        },
    });
};

export const useDisconnectCampaignFacebook = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, void>({
        mutationFn: async () => {
            await httpClient.delete(API_ENDPOINTS.CRM.CAMPAIGNS.FACEBOOK_DISCONNECT);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, FACEBOOK_KEY] });
        },
    });
};

export const useCampaignFacebookPageForms = (pageId: string) => {
    return useQuery<ApiResponse<FacebookForm[]>>({
        queryKey: [CAMPAIGNS_KEY, FACEBOOK_KEY, 'pages', pageId, 'forms'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<FacebookForm[]>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.FACEBOOK_PAGE_FORMS(pageId)
            );
            return data;
        },
        enabled: !!pageId,
    });
};

export const useLinkCampaignFacebookForm = () => {
    const queryClient = useQueryClient();

    return useMutation<Campaign, Error, { id: string; data: LinkFacebookFormRequest }>({
        mutationFn: async ({ id, data }) => {
            const { data: responseData } = await httpClient.post<ApiResponse<Campaign>>(
                API_ENDPOINTS.CRM.CAMPAIGNS.FACEBOOK_LINK_FORM(id),
                data
            );
            return responseData.data;
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, id] });
            queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_KEY, FACEBOOK_KEY] });
        },
    });
};
